import { Feature, View, Map, MapBrowserEvent } from "ol";
import { ImageArcGISRest, OSM, Vector as VectorSource, Cluster as ClusterSource } from "ol/source";
import { Circle } from "ol/geom";
import { Style } from "ol/style";
import { TileWMS } from "ol/source";
import { Tile as TileLayer, Image as ImageLayer, Vector as VectorLayer, Layer } from "ol/layer";
import { config } from "./config";
import { getClusterSymbol, getRouteClusterSymbol, getRouteSymbol, getSymbol, getTrackSymbol } from "./symbols";
import { CustomSubtype, FeatureType, RouteLayers, SelectedStatus } from "./enums";
import { defaults as defaultControls } from "ol/control";
import { BasemapButton } from "./basemapButton";
import { LocationButton } from "./GeoLocation";
import { CompassButton } from "./Compass";
import { HomeButton } from "./Home";
import { getMap, IVisibleLayer } from "./mapProvider";
import { getAllTracks } from "./rest";
import { extend, Extent, buffer } from "ol/extent";
import { unselectGameCheckpoints, unselectGameMainPoints, unselectGameTracks } from "./gameFunctions";
import { getGameSymbol } from "./Game/gameSymbols";

const clusterLayerId = config.defaults.clusterLayerId;
const selectedRouteLayerId = config.defaults.selectedRouteLayerId;
const tracksLayerId = config.defaults.tracksLayerId;

let hoverId = undefined;

const getBuffer = (feature: Feature) => buffer(feature.getGeometry().getExtent(), 100);

export const createMap = (target: HTMLDivElement, clusterSourceRef, texts): Map => {
    const osmLayer = new TileLayer({
        source: new OSM()
    });
    const ortoLayer = new TileLayer({
        source: new TileWMS({
            url: "https://lvmgeoserver.lvm.lv/geoserver/ows",
            params: {
                LAYERS: "public:Orto_LKS",
                FORMAT: "image/vnd.jpeg-png8"
            },
            serverType: "geoserver",
            crossOrigin: "anonymous"
        }),
        visible: false
    });
    const dynamicLayer = new ImageLayer({
        id: config.infra.id,
        source: new ImageArcGISRest({
            ratio: 1,
            params: {},
            url: config.infra.url
        })
    });
    const clusterLayer = new VectorLayer({
        id: clusterLayerId,
        source: new ClusterSource({
            distance: 80,
            source: clusterSourceRef
        }),
        style: (clusterFeature) => {
            const clusterFeatures = clusterFeature.get("features");
            return clusterFeatures.length > 1
                ? getClusterSymbol(clusterFeatures.length)
                : getSymbol(
                      clusterFeatures[0].get("CUSTOMSUBTYPE") &&
                          clusterFeatures[0].get("CUSTOMSUBTYPE") !== CustomSubtype.PasvaldibasObjekts
                          ? clusterFeatures[0].get("CUSTOMSUBTYPE")
                          : clusterFeatures[0].get("PLACESUBTYPE"),
                      clusterFeatures[0].get("VISITED"),
                      clusterFeatures[0].get("ROUTE"),
                      clusterFeatures[0].get("SELECTED")
                  );
        }
    });
    // @ts-ignore
    window.clusterLayer = clusterLayer;

    return new Map({
        layers: [osmLayer, ortoLayer, dynamicLayer, clusterLayer],
        target,
        view: new View({
            projection: "EPSG:3857",
            center: [config.defaults.center[0], config.defaults.center[1]],
            zoom: 7.0670086534273295
        }),
        controls: defaultControls({ zoom: false, rotate: false }).extend([
            new BasemapButton(texts.button.basemap),
            new LocationButton(),
            new CompassButton(texts.button.compass),
            new HomeButton(texts.button.home)
        ])
    });
};

export const onPointerMove = (evt: MapBrowserEvent, map: Map, target: HTMLDivElement): void => {
    if (evt.dragging) {
        return;
    }

    const hasFeatureAtPixel = map.hasFeatureAtPixel(evt.pixel, {
        layerFilter: (layer: Layer) => layer.get("id") === clusterLayerId
    });
    const hasRouteFeatureAtPixel = map.hasFeatureAtPixel(evt.pixel, {
        layerFilter: (layer: Layer) => layer.get("id") === RouteLayers.Route
    });
    const hasRoutePointAtPixel = map.hasFeatureAtPixel(evt.pixel, {
        layerFilter: (layer: Layer) =>
            layer.get("id") === selectedRouteLayerId || layer.get("id") === RouteLayers.RoutePoints
    });
    const hasGameFeatureAtPixel = map.hasFeatureAtPixel(evt.pixel, {
        layerFilter: (layer: Layer) =>
            layer.get("id") === FeatureType.GameMainPoint || layer.get("id") === FeatureType.GameCheckPoint
    });
    target.style.cursor =
        hasFeatureAtPixel || hasRouteFeatureAtPixel || hasRoutePointAtPixel || hasGameFeatureAtPixel ? "pointer" : null;

    if (hasRouteFeatureAtPixel) {
        const features = map.getFeaturesAtPixel(evt.pixel)[0].get("features");
        if (features && features.length === 1) {
            const routeId = features && features.length ? features[0].get("ROUTEID") : undefined;
            if (routeId === undefined) {
                hoverId = routeId;
                setHoverStyle(hoverId, SelectedStatus.Selected);
            } else if (routeId !== hoverId) {
                setHoverStyle(hoverId, SelectedStatus.Default);
                hoverId = routeId;
                setHoverStyle(hoverId, SelectedStatus.Selected);
            }
        }
    } else {
        if (hoverId !== undefined) {
            setHoverStyle(hoverId, SelectedStatus.Default);
            hoverId = undefined;
        }
    }
};

export const setHoverStyle = (routeId: string, selectedStatus: SelectedStatus): void => {
    getLayerById(RouteLayers.RouteTrack)
        .getSource()
        .forEachFeature((feature: Feature) => {
            feature.get("ROUTEID") === routeId &&
                feature.get("SELECTED") !== SelectedStatus.Selected &&
                feature.setStyle(getRouteSymbol(RouteLayers.RouteTrack, selectedStatus));
        });
};

export const getLayerById = (id: string): Layer =>
    getMap() &&
    getMap()
        .getLayers()
        .getArray()
        .find((layer: Layer) => layer.get("id") === id);

export const setLayerVisibility = (layers: IVisibleLayer[]) => {
    layers &&
        layers.length &&
        layers.map((layer) => {
            const editLayer = getLayerById(layer.layerId);
            editLayer && editLayer.setVisible(layer.visibility);
        });
};

export const forEachFeatureOfLayer = (layerId: string, func: CallableFunction): void => {
    const layer = getLayerById(layerId);
    layer && layer.getSource().forEachFeature((f: Feature) => func(f));
};

export const showTrackPoints = (routeId: string): void => {
    getLayerById(RouteLayers.RoutePoints)
        .getSource()
        .forEachFeature((feature: Feature) => {
            feature.set("SELECTED", SelectedStatus.Default);
            feature.set("ROUTE", 0);
            if (feature.get("ROUTEID") === routeId) {
                feature.set("ROUTE", 1);
                feature.set("SELECTED", SelectedStatus.Default); // TODO otreiz uzseto?
            }
        });
};

export const toggleMapControls = (isOn: boolean): void => {
    const compass = document.getElementById("compass-button");
    compass && (compass.style.display = isOn ? "none" : "block");
    const basemap = document.getElementById("basemap-toggle");
    basemap && (basemap.style.display = isOn ? "none" : "block");
    const home = document.getElementById("home-button");
    home && (home.style.display = isOn ? "none" : "block");
};

export const addTracks = async (): Promise<void> => {
    const tracks = await getAllTracks();
    tracks?.length &&
        getMap()
            .getLayers()
            .insertAt(
                2,
                new VectorLayer({
                    id: tracksLayerId,
                    source: new VectorSource({ features: tracks }),
                    visible: true,
                    style: getTrackSymbol
                })
            );
};

export const zoomToFeature = (feature: Feature, callback?: CallableFunction): void => {
    if (!feature) {
        return;
    }
    const zoomToLevel = config.defaults.zoomToLevel;
    const view = getMap().getView();
    const geometry = feature.getGeometry();
    const currentZoom = view.getZoom();
    const maxZoom = currentZoom > zoomToLevel ? currentZoom : zoomToLevel;
    document.dispatchEvent(new Event("stopTracking"));
    view.fit(geometry, { duration: 500, maxZoom, padding: [0, 0, 0, view.paddingLeft], callback });
};

export const zoomToExtent = (extent: Extent, callback?: CallableFunction) => {
    document.dispatchEvent(new Event("stopTracking"));
    const view = getMap().getView();
    view.fit(extent, { duration: 500, padding: [50, 50, 50, 50 + view.paddingLeft], callback });
};

export const handleClusterClick = (clusterFeature: Feature) => {
    const extent = clusterFeature.reduce<Extent>((result, feature) => {
        return result === null ? getBuffer(feature) : extend(result, getBuffer(feature));
    }, null);
    const view = getMap().getView();
    view.fit(extent, { padding: [0, 0, 0, view.paddingLeft], duration: 500 });
};

export const changeLayerVisibility = (layerVisibility) => {
    setLayerVisibility(layerVisibility);
    if (layerVisibility && layerVisibility.length) {
        const isGameMode = !!layerVisibility.find(
            (layer) => layer.layerId === FeatureType.GameMainPoint && layer.visibility === true
        );
        toggleMapControls(isGameMode);
        if (!isGameMode) {
            unselectGameCheckpoints();
            unselectGameMainPoints();
            unselectGameTracks();
        }
    }
};

export const addRouteLayers = (routeLayers, routesSourceRef, selectedSourceRef) => {
    const map = getMap();
    routeLayers.map((features, i) => {
        const id =
            i === 0
                ? RouteLayers.Route
                : i === 1
                ? RouteLayers.Other
                : i === 2
                ? RouteLayers.RoutePoints
                : i === 3
                ? RouteLayers.RouteTrack
                : undefined;
        let source: VectorSource;
        let routeLayer: VectorLayer;
        if (id === RouteLayers.Route) {
            routesSourceRef.addFeatures(features);
            source = new ClusterSource({ distance: 80, source: routesSourceRef });
            routeLayer = new VectorLayer({
                id,
                source,
                style: (clusterFeature) => {
                    const clusterFeatures = clusterFeature.get("features");
                    return clusterFeatures.length > 1
                        ? getRouteClusterSymbol(clusterFeatures.length)
                        : getRouteSymbol(id, clusterFeatures[0].get("SELECTED"));
                }
            });
        } else {
            source = new VectorSource({ features });
            routeLayer = new VectorLayer({
                id,
                source,
                style: (routeFeature) =>
                    getRouteSymbol(
                        id,
                        routeFeature.get("SELECTED"),
                        routeFeature.get("TYPE"),
                        routeFeature.get("ROUTE")
                    )
            });
        }

        id === RouteLayers.RouteTrack
            ? map.getLayers().insertAt(3, routeLayer)
            : id === RouteLayers.RoutePoints
            ? map.getLayers().insertAt(4, routeLayer)
            : map.addLayer(routeLayer);
    });

    const selectedRouteLayer = new VectorLayer({
        id: selectedRouteLayerId,
        source: selectedSourceRef,
        style: (feature) =>
            getSymbol(
                feature.get("PLACESUBTYPE"),
                feature.get("VISITED"),
                feature.get("ROUTE"),
                feature.get("SELECTED")
            )
    });
    map.addLayer(selectedRouteLayer);
};

export const addGameLayers = (gameLayers) =>
    getMap()
        .getLayers()
        .extend(
            gameLayers
                .map((features) =>
                    features.length
                        ? new VectorLayer({
                              id: features[0].get("GAMEPOINTTYPE"),
                              source: new VectorSource({ features }),
                              visible: false,
                              style: (f) =>
                                  getGameSymbol(
                                      f.get("GAMEPOINTTYPE"),
                                      f.get("GAMEPOINTSTATUS"),
                                      f.get("GAMESELECTED"),
                                      f.get("SELECTED"),
                                      f.get("HIDDEN"),
                                      f.get("APPROACHED")
                                  )
                          })
                        : undefined
                )
                .filter((item) => item)
                .reverse()
        );

export const getSelectRouteFeature = (routeId: string): Feature => {
    let routeFeature;
    getLayerById(RouteLayers.Route)
        .getSource()
        .forEachFeature((item: Feature) => {
            const features: Feature[] = item.get("features");
            if (features.length) {
                if (features.length === 1) {
                    if (features[0].get("ROUTEID") === routeId) {
                        routeFeature = features[0];
                    }
                } else {
                    features.find((nestedFeature) => {
                        if (nestedFeature.get("ROUTEID") === routeId) {
                            routeFeature = nestedFeature;
                        }
                    });
                }
            }
        });

    return routeFeature;
};

export const selectTrack = (objectId: string) => {
    const layer = getLayerById(tracksLayerId);
    layer &&
        layer.getSource().forEachFeature((trackFeature: Feature) => {
            trackFeature.get("TOURISMPLACEID") === objectId
                ? trackFeature.set("SELECTED", SelectedStatus.Selected)
                : trackFeature.get("SELECTED") !== SelectedStatus.Default &&
                  trackFeature.set("SELECTED", SelectedStatus.Default);
        });
};

export const unselectRoutePoint = (callback) => {
    getLayerById(RouteLayers.RoutePoints)
        .getSource()
        .forEachFeature((pointFeature: Feature) => {
            if (pointFeature.get("SELECTED") !== SelectedStatus.Default) {
                pointFeature.set("SELECTED", SelectedStatus.Default);
                callback?.(pointFeature.get("OBJECTID"));
            }
        });
};

export const unselectLVMRoutePoint = (selectedRouteFeatures, callback): void => {
    for (const routeFeature of selectedRouteFeatures) {
        if (routeFeature.get("SELECTED") !== SelectedStatus.Default) {
            routeFeature.set("SELECTED", SelectedStatus.Default);
            callback?.(routeFeature.get("OBJECTID"));
        }
    }
};

export const selectOtherSourceRoutePoint = (feature: Feature): void => {
    const otherObjectId = feature.get("SOURCEID");
    const otherFeature = getLayerById(RouteLayers.Other)
        .getSource()
        .getFeatures()
        .find((item: Feature) => item.get("OBJECTID") === otherObjectId);

    if (otherFeature) {
        feature.set("PLACENAME", otherFeature.get("PLACENAME"), true);
        feature.set("PLACEDESCRIPTION", otherFeature.get("PLACEDESCRIPTION"), true);
        feature.set("ISCLOSED", otherFeature.get("ISCLOSED"), true);
    }
};

export const getNearbyCircle = (location: number[]): Feature => {
    const circle = new Feature({ geometry: new Circle([location[0], location[1]], 60000) });
    circle.setStyle(
        new Style({
            renderer(coordinates, state) {
                const [[x, y], [x1, y1]] = coordinates;
                const ctx = state.context;
                const dx = x1 - x;
                const dy = y1 - y;
                const radius = Math.sqrt(dx * dx + dy * dy);

                const innerRadius = 0;
                const outerRadius = radius * 1.4;

                const gradient = ctx.createRadialGradient(x, y, innerRadius, x, y, outerRadius);
                gradient.addColorStop(0, "rgba(255,255,255,0)");
                gradient.addColorStop(0.6, "rgba(255,255,255,0.2)");
                gradient.addColorStop(1, "rgba(255,255,255,0.4)");
                ctx.beginPath();
                ctx.arc(x, y, radius, 0, 2 * Math.PI, true);
                ctx.fillStyle = gradient;
                ctx.fill();

                ctx.arc(x, y, radius, 0, 2 * Math.PI, true);
                ctx.strokeStyle = "rgba(171, 183, 191, 0.5)";
                ctx.stroke();
            }
        })
    );
    return circle;
};
